<app-line-loader *ngIf="loading" [sseConfig]="sseConfig"></app-line-loader>
<div
  *ngIf="!loading"
  class="mt-1 min-h-screen flex justify-center items-center mb-2"
>
  <!-- Render cards if fieldType is 'cards' or status is in progress -->
  <div
    *ngIf="fields.fieldType == 'cards' || isInProgress"
    class="w-full max-w-2xl mx-auto dark-cards-container"
  >
    <!-- In Progress State: Show In Progress Icon, Static Text and Subtext -->
    <div *ngIf="isInProgress" class="in-progress-container">
      <div
        class="flex flex-col items-center justify-center text-center w-full py-8"
      >
        <div class="in-progress-icon-wrapper mx-auto">
          <div class="in-progress-pulse-ring"></div>
          <div class="in-progress-icon-circle">
            <!-- Animated In-Progress Circular Arrow SVG Icon -->
            <svg
              class="animate-spin-slow"
              xmlns="http://www.w3.org/2000/svg"
              width="30"
              height="30"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.8 1.04 6.44 2.73L21 8" />
              <polyline points="21 3 21 8 16 8" />
            </svg>
          </div>
        </div>

        <div class="text-center max-w-lg mx-auto">
          <span
            class="text-md font-semibold tracking-wider text-gray-900 block mb-1"
          >
            Integration is in progress
          </span>
          <h2 class="text-2sm font-bold text-gray-700">
            Your integration is in process. Your assets, action items and tasks
            will fetch automatically
          </h2>
        </div>
      </div>
    </div>

    <!-- Success State: Show Integration Status, Cards Grid & Banner -->
    <ng-container *ngIf="!isInProgress">
      <div class="text-center mb-6">
        <span class="text-md font-semibold tracking-wider text-gray-900">{{
          fields.displayName || "Integration Status"
        }}</span>
        <h2 class="text-2sm font-bold mt-1 text-gray-700">
          {{ fields.helpText || "Here's what we've done for you." }}
        </h2>
      </div>

      <div class="cards-grid">
        <!-- Looping Card Items -->
        <div
          *ngFor="let card of cardsData; let i = index"
          class="metric-card p-3 bg-white dark:bg-coal-500"
          [ngClass]="
            card.type === 'asset'
              ? 'blue-card'
              : card.type === 'actionItem'
                ? 'purple-card'
                : 'green-card'
          "
        >
          <div>
            <div class="flex flex-row gap-3 border-0 mb-1 p-2 items-center">
              <div
                class="icon-wrapper"
                [ngClass]="
                  card.type === 'asset'
                    ? 'blue-icon'
                    : card.type === 'actionItem'
                      ? 'purple-icon'
                      : 'green-icon'
                "
              >
                <!-- Custom image URL or base64 -->
                <img
                  *ngIf="card?.image || card?.iconUrl"
                  [src]="card?.image || card?.iconUrl"
                  class="w-5 h-5 object-contain"
                />

                <!-- Font icon class e.g. fa fa-database -->
                <i
                  *ngIf="
                    card?.icon &&
                    !card?.icon?.includes('<svg') &&
                    !card?.image &&
                    !card?.iconUrl
                  "
                  [class]="card.icon"
                  class="text-xl"
                ></i>

                <!-- Inline SVG markup -->
                <span
                  *ngIf="card?.icon && card?.icon?.includes('<svg')"
                  [innerHTML]="card.icon"
                ></span>

                <!-- Fallback default SVGs -->
                <ng-container
                  *ngIf="!card?.image && !card?.iconUrl && !card?.icon"
                >
                  <!-- SVG Database/Cylinder Icon for asset -->
                  <svg
                    *ngIf="card.type === 'asset'"
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path
                      d="M12 22c5.523 0 10-2.239 10-5V5c0-2.761-4.477-5-10-5S2 2.239 2 5v12c0 2.761 4.477 5 10 5z"
                    />
                    <path d="M2 12c0 2.761 4.477 5 10 5s10-2.239 10-5" />
                    <path d="M2 7c0 2.761 4.477 5 10 5s10-2.239 10-5" />
                  </svg>
                  <!-- SVG Clipboard/List Icon for actionItem -->
                  <svg
                    *ngIf="card.type === 'actionItem'"
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path
                      d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"
                    />
                    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                  </svg>
                  <!-- SVG Shield/Check Icon for task -->
                  <svg
                    *ngIf="card.type === 'task'"
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <path d="m9 11 2 2 4-4" />
                  </svg>
                </ng-container>
              </div>
              <div class="flex flex-col">
                <div
                  class="metric-value flex items-center text-lg"
                  [ngClass]="
                    card.type === 'asset'
                      ? 'blue-value'
                      : card.type === 'actionItem'
                        ? 'purple-value'
                        : 'green-value'
                  "
                >
                  {{ card?.value ?? card?.count ?? 0 }}
                </div>
                <span class="card-title text-sm mt-1 text-gray-800">
                  {{
                    card?.title ||
                      (card.type === "asset"
                        ? "Resources Fetched"
                        : card.type === "actionItem"
                          ? "Action Items Identified"
                          : "Tasks Applicable")
                  }}
                </span>
              </div>
            </div>
            <p class="card-desc text-2sm text-gray-600">
              {{
                card?.description ||
                  (card.type === "asset"
                    ? "We scanned and fetched the available resources related to this integration."
                    : card.type === "actionItem"
                      ? "We identified action items based on best practices and security benchmarks."
                      : "We created compliance tasks to guide you through the process.")
              }}
            </p>
          </div>
          <div
            class="card-footer-text"
            *ngIf="card?.lastUpdated || card?.last_updated"
          >
            Last updated: {{ card?.lastUpdated || card?.last_updated }}
          </div>
        </div>
      </div>

      <!-- Banner Card: You're all set! -->
      <div class="banner-card bg-white dark:bg-coal-500 p-3">
        <div class="banner-left">
          <div class="banner-icon">
            <!-- SVG Shield Check Icon -->
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              <path d="m9 11 2 2 4-4" />
            </svg>
          </div>
          <div class="banner-content flex flex-col">
            <span class="text-sm text-gray-900 mb-1 font-semibold"
              >You're all set!</span
            >
            <span class="text-2sm text-gray-600">
              Your integration is now active. You can explore the insights and
              take action from the dashboard.
            </span>
          </div>
        </div>
      </div>
    </ng-container>
  </div>

  <!-- Default Placeholder -->
  <div
    *ngIf="fields.fieldType !== 'cards' && !isInProgress"
    class="placeholder flex flex-col items-center text-center"
  >
    <img
      [src]="
        fields.uiOptions.image
          ? fields.uiOptions?.image
          : './assets/media/illustrations-image/illustration-2.png'
      "
      class="mb-4 h-250px"
    />
    <span class="text-md text-gray-900 mb-2 font-semibold">{{
      fields.displayName
    }}</span>
    <span class="text-sm text-gray-500">{{ fields.helpText }}</span>
  </div>
</div>
